<template>
    <section
        class="overflow-hidden rounded-3xl border border-primary-medium bg-primary-light shadow-2xl ring-1 ring-primary-medium/20"
        aria-labelledby="booking-rules-title"
    >
        <div class="bg-gradient-to-r from-dark via-primary-dark to-primary-dark px-4 py-3 text-primary-lighter sm:px-5 sm:py-4">
            <div class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                <div class="space-y-1.5">
                    <span
                        class="inline-flex rounded-full border border-primary-lighter/15 bg-white/10 px-2.5 py-0.5 text-[10px] font-semibold tracking-[0.22em] text-primary-light uppercase"
                    >
                        {{ t('reservation.step4.bookingRules') }}
                    </span>
                    <h3 id="booking-rules-title" class="text-lg font-bold tracking-tight sm:text-xl">
                        {{ t('reservation.step4.title') }}
                    </h3>
                    <p class="max-w-2xl text-xs leading-5 text-primary-light/80 sm:text-sm sm:leading-6">
                        {{ t('reservation.step4.intro') }}
                    </p>
                </div>

                <div class="w-full max-w-[12.5rem] rounded-2xl border border-primary-lighter/15 bg-white/10 p-2.5 shadow-lg backdrop-blur-sm sm:p-3">
                    <p class="text-[10px] font-semibold tracking-[0.22em] text-primary-light/80 uppercase">
                        {{ t('reservation.step4.route') }}
                    </p>
                    <div class="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs font-semibold text-primary-lighter sm:text-sm">
                        <span class="max-w-[9rem] truncate rounded-full bg-white/10 px-2.5 py-0.5">{{ fromLabel }}</span>
                        <span aria-hidden="true" class="text-primary-light/70">→</span>
                        <span class="max-w-[9rem] truncate rounded-full bg-white/10 px-2.5 py-0.5">{{ dropToLabel }}</span>
                    </div>
                    <p class="mt-1.5 text-[10px] leading-4 text-primary-light/75">
                        {{ t('reservation.step4.routeHint') }}
                    </p>
                </div>
            </div>
        </div>

        <div class="space-y-5 p-5 sm:p-6">
            <div class="grid gap-4 md:grid-cols-2">
                <article
                    v-for="rule in ruleCards"
                    :key="rule.index"
                    :class="[
                        'group flex gap-4 rounded-2xl border p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5',
                        rule.accent
                            ? 'border-amber-200 bg-amber-50 text-amber-950 ring-1 ring-amber-200/60'
                            : 'border-primary-medium/70 bg-white text-dark hover:border-primary-medium',
                    ]"
                >
                    <div
                        :class="[
                            'flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm font-bold transition-colors duration-200',
                            rule.accent ? 'bg-amber-200 text-amber-950' : 'bg-primary-dark text-primary-lighter group-hover:bg-primary',
                        ]"
                    >
                        {{ String(rule.index + 1).padStart(2, '0') }}
                    </div>

                    <div class="min-w-0 flex-1">
                        <p class="text-sm leading-6 font-medium" :class="rule.accent ? 'text-amber-950' : 'text-dark'">
                            {{ rule.text }}
                        </p>
                    </div>
                </article>
            </div>

            <div v-if="isBeauvaisTrip" class="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-950 shadow-inner">
                <p class="text-sm font-semibold">
                    {{ t('reservation.step4.beauvaisNoticeTitle') }}
                </p>
                <p class="mt-1 text-sm leading-6">
                    {{ t('reservation.step4.beauvaisNoticeText') }}
                </p>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { useTranslations } from '@/composables/useTranslations';
import { computed } from 'vue';

const { t } = useTranslations();

interface Props {
    from: string;
    dropTo: string;
}

const props = defineProps<Props>();

const isBeauvaisTrip = computed(() => {
    const from = props.from.toLowerCase();
    const dropTo = props.dropTo.toLowerCase();

    return from.includes('beauvais') || dropTo.includes('beauvais') || from.includes('bva') || dropTo.includes('bva');
});

function getLocationLabel(locationKey: string) {
    const translated = t(`locations.${locationKey}`);
    return translated === `locations.${locationKey}` ? locationKey : translated;
}

const fromLabel = computed(() => (props.from ? getLocationLabel(props.from) : t('reservation.step1.selectDeparture')));
const dropToLabel = computed(() => (props.dropTo ? getLocationLabel(props.dropTo) : t('reservation.step1.selectDestination')));

const rulesToDisplay = computed(() => {
    const rules = [
        t('reservation.step4.rules.rule1'),
        t('reservation.step4.rules.rule2'),
        t('reservation.step4.rules.rule3'),
        t('reservation.step4.rules.rule4'),
    ];

    if (isBeauvaisTrip.value) {
        rules.push(t('reservation.step4.rules.rule5'));
    }

    return rules;
});

const ruleCards = computed(() =>
    rulesToDisplay.value.map((text, index) => ({
        text,
        index,
        accent: isBeauvaisTrip.value && index === rulesToDisplay.value.length - 1,
    })),
);
</script>
